import type { SyncStatus } from "@homing/core/client";
import type { StatusItem } from "@homing/ui-web";

const time = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
const weekday = new Intl.DateTimeFormat("en-US", { weekday: "short" });
const monthDay = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
const full = new Intl.DateTimeFormat("en-US", { year: "numeric", month: "short", day: "numeric" });

/** List-row date: "14:02" today, "Mon" this week, "Oct 28" this year, else "Oct 28, 2024". */
export function shortWhen(ms: number, now = Date.now()): string {
  const d = new Date(ms);
  const n = new Date(now);
  if (d.toDateString() === n.toDateString()) return time.format(d);
  if (now - ms < 6 * 86_400_000 && ms <= now) return weekday.format(d);
  if (d.getFullYear() === n.getFullYear()) return monthDay.format(d);
  return full.format(d);
}

export function longWhen(ms: number): string {
  return `${full.format(new Date(ms))} · ${time.format(new Date(ms))}`;
}

const int = new Intl.NumberFormat("en-US");

/** The mono status line: phase, backfill progress, pending, offline. */
export function statusItems(s: SyncStatus | null, opts: { blocked?: boolean; error?: string | null } = {}): StatusItem[] {
  if (opts.error) return [{ text: "local database unavailable", alert: true }, { text: opts.error }];
  if (opts.blocked) return [{ text: "open in another tab", alert: true }, { text: "waiting for it to close" }];
  if (!s) return [{ text: "opening local copy" }];
  const items: StatusItem[] = [];
  if (!s.online) items.push({ text: "offline", alert: true }, { text: "showing local copy" });
  else if (s.phase === "reauth") items.push({ text: "sign in again", alert: true });
  else if (s.phase === "syncing") items.push({ text: "syncing" });
  else if (s.phase === "error") items.push({ text: "retrying", alert: true }, ...(s.error ? [{ text: s.error.slice(0, 80) }] : []));
  else if (s.server?.phase === "reauth_required") items.push({ text: "reconnect google", alert: true });
  else items.push({ text: s.connected ? "live" : "up to date" });
  const srv = s.server;
  if (srv?.phase === "backfill" || srv?.phase === "initial") {
    const done = srv.backfilled !== undefined ? int.format(srv.backfilled) : "…";
    items.push({ text: srv.estimatedTotal ? `backfilling ${done} / ~${int.format(srv.estimatedTotal)}` : `backfilling ${done}` });
  }
  if (s.pending > 0) items.push({ text: `${s.pending} pending` });
  if (s.lastSyncAt) items.push({ text: `synced ${time.format(new Date(s.lastSyncAt))}` });
  return items;
}
